import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';

const Pagination = ({ 
  currentPage, 
  totalItems, 
  pageSize, 
  setPage, 
  setPageSize, 
  onPageChange,
  extraInfo
}) => {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const [customSize, setCustomSize] = useState('');
  const [showCustom, setShowCustom] = useState(false);
  const { t } = useTranslation();

  const handleSizeChange = (val) => {
    if (val === 'custom') {
      setShowCustom(true);
      return;
    }
    setShowCustom(false);
    const size = parseInt(val, 10);
    if (!isNaN(size) && size > 0) {
      setPageSize(size);
      setPage(1);
      if (onPageChange) onPageChange(1, size);
    }
  };

  const handleCustomSizeSubmit = (e) => {
    e.preventDefault();
    const size = parseInt(customSize, 10);
    if (!isNaN(size) && size > 0) {
      setShowCustom(false);
      setPageSize(size);
      setPage(1);
      if (onPageChange) onPageChange(1, size);
    }
    setCustomSize('');
  };

  // Always show pagination bar (items-per-page selector + nav when more than 1 page)

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
      {/* Page Size Selector */}
      <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
        <span>{t('components.pagination.items_per_page')}:</span>
        <select 
          value={pageSize} 
          onChange={(e) => handleSizeChange(e.target.value)}
          className="p-1.5 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-slate-800 transition-all dark:text-white"
        >
          {[10, 20, 40, 100].map(size => (
            <option key={size} value={size}>{size}</option>
          ))}
          <option value="custom">{t('components.pagination.custom')}</option>
        </select>

        {showCustom ? (
          <form onSubmit={handleCustomSizeSubmit} className="flex gap-1">
            <input 
              type="number"
              value={customSize}
              onChange={(e) => setCustomSize(e.target.value)}
              placeholder={t('components.pagination.quantity')}
              className="w-16 p-1.5 border border-slate-300 dark:border-slate-600 rounded-lg outline-none focus:ring-2 focus:ring-blue-500 text-center dark:bg-slate-800 dark:text-white"
            />
            <button type="submit" className="px-2 py-1.5 bg-slate-100 dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors text-xs dark:text-white">
              {t('components.pagination.confirm')}
            </button>
          </form>
        ) : null}
      </div>

      {/* Extra info (e.g., product/SKU stats) */}
      {extraInfo && (
        <span className="text-sm text-slate-400 dark:text-slate-500 whitespace-nowrap">
          {extraInfo}
        </span>
      )}

      {/* Navigation Controls (always visible) */}
      <div className="flex items-center gap-2">
        <button 
          onClick={() => setPage(1)} 
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:text-white"
          title={t('components.pagination.first_page')}
        >
          <ChevronsLeft size={18} />
        </button>
        <button 
          onClick={() => setPage(prev => Math.max(1, prev - 1))} 
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:text-white"
          title={t('components.pagination.previous_page')}
        >
          <ChevronLeft size={18} />
        </button>
        
        <span className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">
          {t('components.pagination.page_of', { current: currentPage, total: totalPages })}
          <span className="mx-2 text-slate-300 dark:text-slate-600">|</span>
          {t('components.pagination.total_items', { count: totalItems })}
        </span>

        <button 
          onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} 
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:text-white"
          title={t('components.pagination.next_page')}
        >
          <ChevronRight size={18} />
        </button>
        <button 
          onClick={() => setPage(totalPages)} 
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors dark:text-white"
          title={t('components.pagination.last_page')}
        >
          <ChevronsRight size={18} />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
